/**
 * Los mensajes del chat interno sin leer, para el globo del menú (23/9/2026).
 *
 * Suma las dos salas (la global y, a quien la tiene, la de dirección): el
 * número lo da el servidor en `/api/chat/salas`, que es quien sabe qué salas
 * son de cada persona y hasta dónde ha leído cada una.
 *
 * Se pregunta cada 30 segundos, y solo con la pestaña a la vista: una
 * pestaña olvidada en segundo plano no tiene por qué estar consultando toda
 * la tarde. Al volver a ella, se pregunta en el acto.
 *
 * La pantalla del chat avisa con `avisarChatSinLeer` cuando cambia algo
 * (ha marcado una sala como leída, o acaba de traer el recuento): así el
 * globo se apaga al leer, sin esperar a la siguiente vuelta.
 *
 * Si una consulta falla, el globo se queda como estaba: un fallo de red no
 * es «no hay nada sin leer».
 */
import { useEffect, useState } from 'react';
import { api } from './api';

export const EVENTO_CHAT_SIN_LEER = 'cp:chat-sin-leer';
const CADA_CUANTO_MS = 30000;

// Las claves del chat en la sesión del navegador (el borrador y el envío
// pendiente de cada sala, pages/ChatPage.jsx) empiezan por aquí.
export const PREFIJO_SESION_CHAT = 'cp_chat_';

/** Borra de la sesión del navegador todo lo del chat. Al cerrar sesión
 *  (tercera pasada del 23/9/2026): un borrador a medias es de quien lo
 *  escribía, y en un ordenador compartido no tiene que quedarse ahí para
 *  el siguiente. Si el navegador no deja tocar la sesión, no pasa nada. */
export function olvidarBorradoresDelChat() {
  try {
    const claves = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const clave = window.sessionStorage.key(i);
      if (clave && clave.startsWith(PREFIJO_SESION_CHAT)) claves.push(clave);
    }
    claves.forEach((clave) => window.sessionStorage.removeItem(clave));
  } catch { /* sin almacenamiento: no había nada que borrar */ }
}

/** La pantalla del chat avisa al globo. Con `total`, ese es el número; sin
 *  él, el globo vuelve a preguntar. */
export function avisarChatSinLeer(total) {
  try {
    window.dispatchEvent(new CustomEvent(EVENTO_CHAT_SIN_LEER, {
      detail: { total: Number.isFinite(total) ? total : null },
    }));
  } catch { /* sin CustomEvent (navegador muy viejo): ya preguntará el globo */ }
}

/** Cuántos mensajes sin leer hay, sumando las salas de esta persona.
 *  `activo` = false (quien no tiene chat): no pregunta nada y vale 0. */
export function useChatSinLeer(activo) {
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!activo) {
      setTotal(0);
      return undefined;
    }
    let vivo = true;
    const consultar = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      api.get('/api/chat/salas', { timeout: 15000 })
        .then((r) => {
          const n = Number(r.data?.no_leidos_total);
          if (vivo && Number.isFinite(n)) setTotal(n);
        })
        .catch(() => { /* se queda el número de antes; se reintenta en 30 s */ });
    };
    const alAviso = (e) => {
      const n = e?.detail?.total;
      if (Number.isFinite(n)) setTotal(n);
      else consultar();
    };
    const alVolver = () => {
      if (document.visibilityState === 'visible') consultar();
    };

    consultar();
    const id = setInterval(consultar, CADA_CUANTO_MS);
    window.addEventListener(EVENTO_CHAT_SIN_LEER, alAviso);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      vivo = false;
      clearInterval(id);
      window.removeEventListener(EVENTO_CHAT_SIN_LEER, alAviso);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [activo]);

  return total;
}
